<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row v-if="model">

                <rkm-col cols="12" v-if="exibeAlertaParentescoPendente">
                    <rkm-alert color="warning">
                        <span>
                            O responsável familiar deste núcleo foi alterado. Atualize o grau de parentesco
                            deste paciente em relação ao novo responsável familiar.
                        </span>
                    </rkm-alert>
                </rkm-col>

                <rkm-col cols="12">

                    <rkm-area
                        title="Relação de parentesco com o responsável familiar"
                        type="fieldset"
                        no-margin-top
                    >

                        <rkm-row>

                            <rkm-col cols="6">
                                <rkm-form-select
                                    :options="form.parentesco"
                                    :label="false"
                                    show-default-option
                                    text-field="descricao"
                                    v-model="model.parentescoResponsavelId"
                                    default-option-text="Selecione"
                                    value-field="id"
                                    class="pt-0"
                                    :disabled="model.responsavel ? true : false"
                                >
                                </rkm-form-select>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area
                        title="Residentes atuais"
                        type="fieldset"
                    >

                        <rkm-row class="flex-nowrap" v-if="form.domicilioId">
                            <rkm-col cols="4">

                                <rkm-form-input-autocomplete
                                    label="Nome"
                                    v-model="paciente"
                                    :repository="repositories.paciente"
                                    :search-fields="fields.autocomplete.paciente"
                                    :text-field="(item) =>  item.cidadao.cns ? `${this.formatCns(item.cidadao.cns)} - ${item.cidadao.nome}` : item.cidadao.nome"
                                    :validations="validations.paciente"
                                    uppercase
                                />
                            </rkm-col>

                            <rkm-col cols="4">
                                <rkm-form-input
                                    :value="getSafely(() => paciente.cidadao.nomeSocial)"
                                    disabled
                                    label="Nome social"
                                />
                            </rkm-col>

                            <rkm-col class="flex-grow-1">
                                <rkm-form-date-picker
                                    :value="getSafely(() => paciente.cidadao.dataNascimento)"
                                    disabled
                                    label="Data de nascimento"
                                />
                            </rkm-col>

                            <rkm-col class="flex-grow-1">
                                <rkm-form-input
                                    :value="getSafely(() => paciente.prontuario)"
                                    disabled
                                    label="Prontuário municipal"
                                />
                            </rkm-col>

                            <rkm-col cols="auto">
                                <rkm-button
                                    :label="false"
                                    color="primary"
                                    :disabled="paciente && paciente.id ? false : true"
                                    @click="save"
                                >
                                    Incluir
                                </rkm-button>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="12">

                                <rkm-table
                                    :fields="fields.table"
                                    :items="form.residentes"
                                    ref="Table"
                                    sort-by="nome"
                                >

                                    <template v-slot:cell(cns)="row">
                                        {{ row.value | formatCns }}
                                    </template>

                                    <template v-slot:cell(nome)="{ value, item }">
                                        {{ value }}
                                        <rkm-icon
                                            v-if="item.isResponsavel"
                                            icon="crown"
                                            title="Responsável familiar"
                                        />
                                    </template>

                                    <template v-slot:cell(actions)="{ item }">
                                        <rkm-button
                                            @click.stop="$refs.Modal.open(item.pacienteId)"
                                            :disabled="item.pacienteId === model.id"
                                            color="primary"
                                            title="Editar"
                                            small
                                        >
                                            <rkm-icon icon="pen" />
                                        </rkm-button>

                                        <rkm-button
                                            @click.stop="remove(item)"
                                            :disabled="item.pacienteId === model.id"
                                            color="danger"
                                            title="Excluir"
                                            small
                                        >
                                            <rkm-icon icon="trash" />
                                        </rkm-button>

                                    </template>
                                </rkm-table>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <Modal
            ref="Modal"
            :data="{ form, model }"
        />

        <ModalResponsavel
            ref="ModalResponsavel"
            :data="{ form, model }"
            @remove="removeResponsavel"
        />
    </div>
</template>

<script>

// Enums.
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import permissao from '@/enums/atencao-basica/parametrizacao/permissao'
import tipoAtendimento from '@/enums/atencao-basica/atendimento-individual/tipoAtendimento'
import saidaCidadao from '@/enums/atencao-basica/saidaCidadao'
import tipoImovel from '@/enums/atencao-basica/cadastro-domiciliar/tipoImovel'
import zonaDomicilio from '@/enums/atencao-basica/cadastro-domiciliar/zonaDomicilio'

// Filters.
import { mapGetters, mapActions } from 'vuex'

// Vuex.
import format from '@/filters/formats'

// Mixins
import { repository, object, formats, stringCase } from '@/mixins'

import Modal from '@/components/cadastro/paciente/components/cadastro-residente/Modal'
import ModalResponsavel from '@/components/cadastro/paciente/tabs/components/responsavel/Modal'

export default {
    components: { Modal, ModalResponsavel },
    mixins: [ repository, object, formats, stringCase ],
    props: {
        form: { type: Object, required: true },
        model: { type: Object, required: true },
    },
    created() {
        this.repositories = {
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
        }
    },
    data: () => ({
        enums: {
            classificacao,
            subclassificacao,
            permissao,
            tipoAtendimento,
            saidaCidadao,
            tipoImovel,
            zonaDomicilio
        },
        paciente: null,
        unidades: [],
        equipes: [],
        infraestruturas: [],
        ocupacoes: [],
        localForm: {
            autocompleteFields: {
                ocupacao: [
                    { key: 'codigo', searchable: true },
                    { key: 'nome', searchable: true },
                ],
                comunidade: [
                    { key: 'codigoEsus', searchable: true },
                    { key: 'descricao', searchable: true },
                ],
            },
        },
    }),
    computed: {
        ...mapGetters({ cliente: 'getCliente' }),
        validations() {

            return {
                classificacao: { required: true },
                subclassificacao: {},
                prontuarioUnidade: { max: 10 },
                unidadeReferencia: { required: true },
                responsavelUnidade: { required: true },
                usuario: { required: true },
                infraestrutura: { required: true },
                equipe: { required: true },
                ocupacao: { required: true },
                data: { required: true },
                responsavelFamiliar: { required: true },
            }
        },
        autocompleteRequestParams() {
            return {
                usuario: {
                    with_not_regularized: true,
                    with_atencao_basica_equipe: true,
                    unidade_id: this.model.unidadeId,
                    with_atencao_basica_permissao: this.enums.permissao.CADASTRO_INDIVIDUAL,
                },
            }
        },
        fields() {
            return {
                autocomplete: {
                    paciente: [
                        {key: 'cidadao.nome', searchable: true},
                    ],
                },
                table: [
                    {
                        key: 'nome',
                        label: 'Nome',
                        sortable: true
                    },
                    {
                        key: 'nomeSocial',
                        label: 'Nome social',
                        sortable: true
                    },
                    {
                        key: 'dataNascimento',
                        label: 'Data nascimento ',
                        class: 'col-min text-center',
                        sortable: true
                    },
                    {
                        key: 'cns',
                        label: 'CNS',
                        class: 'col-min text-center',
                        sortable: true
                    },
                    {
                        key: 'prontuario',
                        label: 'Prontuário',
                        class: 'col-min text-center',
                        sortable: true
                    },
                    {
                        key: 'parentesco',
                        label: 'Grau de parentesco',
                        class: 'col-min',
                        sortable: true
                    },
                    {
                        key: 'responsavelFamiliar',
                        label: 'Responsável familiar',
                        class: 'col-min',
                        sortable: true
                    },
                    {
                        name: 'actions',
                        key: 'actions',
                        label: 'Ações',
                        class: 'col-min'
                    },
                ],
            }
        },
        exibeAlertaParentescoPendente() {

            return !this.model.responsavel
                && !!this.form.domicilioId
                && !this.model.parentescoResponsavelId
                && this.form.responsaveis
                && this.form.responsaveis.length > 0
        },
    },
    filters: {
        ...format,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        validate() {

            return this.$refs.Form.validate()
        },
        save() {

            this.setLoading()

            const request = {
                cidadaoId: this.paciente.cidadao.id,
                domicilioId: this.form.domicilioId,
                unidadeId: this.model.unidadeId,
                classificacaoId: this.model.classificacaoId,
                responsavelUsuarioId: this.model.responsavelUsuario ? this.model.responsavelUsuario.id : null,
                responsavelOcupacaoId: this.model.responsavelOcupacaoId,
                responsavelEquipeId: this.model.responsavelEquipeId,
                responsavelData: this.model.responsavelData,
                imovelTipoId: this.model.imovelTipoId ?? null,
                situacaoDomicilioId: this.model.situacaoDomicilioId ?? null,
                domicilioTipoId: this.model.domicilioTipoId ?? null,
                quantidadeComodos: this.model.quantidadeComodos ?? null,
                situacaoRuralId: this.model.situacaoRuralId ?? null,
                domicilioZonaId: this.model.domicilioZonaId ?? null,
                domicilioTipoAcessoId: this.model.domicilioTipoAcessoId ?? null,
                materialPredominanteId: this.model.materialPredominanteId ?? null,
                abastecimentoId: this.model.abastecimentoId ?? null,
                tratamentoId: this.model.tratamentoId ?? null,
                destinoLixoId: this.model.destinoLixoId ?? null,
                formaEscoamentoId: this.model.formaEscoamentoId ?? null,
                possuiAnimais: this.model.possuiAnimais ? true : false,
                energiaEletrica: this.model.energiaEletrica ? true : false,
                quantidadeAnimais: this.model.quantidadeAnimais ?? null,
                animal: this.model.animal ?? false,
                instituicaoNome: this.model.instituicaoNome ?? null,
                instituicaoOutrosProfissionais: this.model.instituicaoOutrosProfissionais ?? null,
                instituicaoNomeResponsavelTecnico: this.model.instituicaoNomeResponsavelTecnico ?? null,
                instituicaoCnsResponsavelTecnico: this.model.instituicaoCnsResponsavelTecnico ?? null,
                instituicaoCargoResponsavelTecnico: this.model.instituicaoCargoResponsavelTecnico ?? null,
                instituicaoTelefoneResponsavelTecnico: this.model.instituicaoTelefoneResponsavelTecnico ?? null,
                escalaCoelhoId: this.model.escalaCoelhoId ?? null,
                higieneResidencialId: this.model.higieneResidencialId ?? null,
                bolsaFamilia: this.model.bolsaFamilia ?? false,
                valeGas: this.model.valeGas ?? false,
                bolsaEscola: this.model.bolsaEscola ?? false,
                lazer: this.model.lazer,
                bpc: this.model.bpc ?? false,
                foraArea: this.model.foraArea ?? false,
                microareaId: this.model.microareaId ?? null,
            }

            this.repositories.paciente.saveResidente(this.camelObjectKeysToSnakeRecursive(request))
                .then(({ data }) => this.form.residentes = data)
                .catch(this.handleError)
                .finally(() => this.unsetLoading(this.resetForm()))
        },
        resetForm() {

            this.paciente = null
        },
        remove(item) {

            if (item.isResponsavel) {

                this.handleResponsavel(item)

            } else {

                this.handleRemove(item)
            }
        },
        handleRemove(item) {

            const remove = () => {

                this.setLoading()

                return this.repositories.paciente.removeResidente(item.id)
                    .then(({ data }) => {
                        this.form.residentes = data.residentes
                        this.form.residentesAnteriores = data.residentesAnteriores
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }

            this.showConfirmation(
                `O paciente será desvinculado do profissional, equipe, unidade e local. O cadastro ficará Provisório
                e o endereço será limpo. Ao desvincular este paciente, ele deixará de compor este núcleo familiar.
                Os dados anteriores serão mantidos para histórico e auditoria. Deseja continuar?`,
                remove,
            )
        },
        removeResponsavel(item) {

            this.setLoading()

            return this.repositories.paciente.removeResidente(item.id)
                .then(({ data }) => {
                    this.form.residentes = data.residentes
                    this.form.residentesAnteriores = data.residentesAnteriores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleResponsavel(item) {

            const desvincular = () => {

                this.setLoading()

                return this.repositories.paciente.removeResidente(item.id, {remove_familia: true})
                    .then(({ data }) => {
                        this.form.residentes = data.residentes
                        this.form.residentesAnteriores = data.residentesAnteriores
                        this.clearFields()
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }

            this.showConfirmation(
                `Você está removendo o Responsável Familiar. Deseja desvincular toda a família?`,
                desvincular,
                async () => this.openModalResponsavel(item),
            )
        },
        clearFields() {

            this.model.cidadao.domicilio.endereco.logradouro.cep = null
            this.model.cidadao.domicilio.endereco.numero = null
            this.model.cidadao.domicilio.endereco.complemento = null
            this.model.cidadao.domicilio.endereco.latitude = null
            this.model.cidadao.domicilio.endereco.longitude = null
            this.model.cidadao.domicilio.endereco.loteamento = null
            this.model.cidadao.domicilio.endereco.zonaId = null
            this.model.cidadao.domicilio.endereco.referencia = null
            this.model.cidadao.domicilio.endereco.logradouro.cep = null
            this.model.cidadao.domicilio.endereco.logradouro.id = null
            this.model.cidadao.domicilio.endereco.logradouro.nome = null
            this.model.cidadao.domicilio.endereco.logradouro.tipoNome = null
            this.model.cidadao.domicilio.endereco.logradouro = null

            this.form.cidadao.endereco.cep = ''
        },
        openModalResponsavel(item){

            this.$refs.ModalResponsavel.open(item)
        }
    }
}
</script>